import { useQuery } from '@tanstack/react-query';
import { getChannelHealth, getSystemHealth } from '../../lib/api.js';
import { type DrawerSection, deriveAlerts } from '../../lib/health-status.js';
import { useAlertDock } from '../../lib/use-alert-dock.js';

/**
 * R9 — the in-frame alert dock (shell grid row 2). Replaces the two stand-alone
 * banners with discrete, per-condition items the operator can Acknowledge or
 * Snooze (each re-raises when the condition changes / returns), plus a brief
 * resolved-✓ when one clears. Renders nothing when there's nothing to say, so
 * the grid row collapses to zero height. Reads the SAME `deriveAlerts` the
 * status pill uses, so the dock and the badge always agree.
 */
export function AlertDock({ onOpenDrawer }: { onOpenDrawer: (section?: DrawerSection) => void }) {
  const channelQ = useQuery({
    queryKey: ['channel-health'],
    queryFn: ({ signal }) => getChannelHealth({ signal }),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  const systemQ = useQuery({
    queryKey: ['system-health'],
    queryFn: ({ signal }) => getSystemHealth({ signal }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const alerts = deriveAlerts(channelQ.data, systemQ.data);
  const { visible, resolved, acknowledge, snooze } = useAlertDock(alerts);

  if (visible.length === 0 && resolved.length === 0) return null;

  return (
    <section className="app-alertdock" data-testid="alert-dock" aria-label="System alerts">
      {visible.map((a) => (
        <div
          key={a.id}
          className={`app-alert app-alert-${a.severity}`}
          data-testid={`alert-${a.id}`}
          role="alert"
        >
          <span className="app-alert-icon" aria-hidden>
            {a.severity === 'red' ? '⛔' : '⚠'}
          </span>
          <span className="app-alert-text">
            <strong>{a.title}</strong> — {a.detail}
          </span>
          <span className="app-alert-actions">
            <button
              type="button"
              onClick={() => onOpenDrawer(a.section)}
              data-testid={`alert-${a.id}-view`}
            >
              {a.actionLabel}
            </button>
            <button type="button" onClick={() => snooze(a.id)} data-testid={`alert-${a.id}-snooze`}>
              Snooze 1h
            </button>
            <button
              type="button"
              onClick={() => acknowledge(a.id)}
              data-testid={`alert-${a.id}-ack`}
            >
              Acknowledge
            </button>
          </span>
        </div>
      ))}
      {resolved.map((r) => (
        <div key={r.id} className="app-alert app-alert-resolved" data-testid={`resolved-${r.id}`}>
          <span className="app-alert-icon" aria-hidden>
            ✓
          </span>
          <span className="app-alert-text">Resolved — {r.title}</span>
        </div>
      ))}
    </section>
  );
}
